import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiServiceInstance } from '../utils/services/ApiService';
import { eventServiceInstance } from '../utils/services/EventService';
import { SmartOptinsForm } from './SmartOptinsCollect.style';
import { SmartOptinsNoFormScreen } from '../templates/screens/SmartOptinsNoFormScreen';
import { SmartOptinsPostFormScreen } from '../templates/screens/SmartOptinsPostFormScreen';
import { collectServiceInstance } from '../utils/services/CollectService';
import { CollectFormValues } from '../common-types/CollectForm.type';
import { ErrorScreen } from '../templates/screens/ErrorScreen';
import { getDefaultConfigurations } from '../utils/Configs';
import { Logger } from '../utils/Logger';
import { smartOptinsServiceInstance } from '../utils/services/SmartOptinsExperienceService';
import { setSmartOptins } from '../slices/SmartOptins';
import { useAppDispatch, useAppSelector } from '../hooks';
import { personalDataServiceInstance } from '../utils/services/PersonalDataService';
import { setRetailerPersonalData } from '../slices/GlobalRetailerPersonalData';
import { setInitialExperienceData } from '../slices/GlobalExperience';
import { Loader } from '../templates/common-components/Loader';
import { transitionAfterCollectScreen, transitionAfterWelcomeScreen } from '../slices/DisplayedScreen';
import { experienceServiceInstance } from '../utils/services/ExperienceService';
import { datadogLogs } from '@datadog/browser-logs';

enum SMART_OPTINS_SCREENS {
  FORM,
  NO_FORM,
  POST_FORM,
}

export const Collect: React.FunctionComponent = () => {

  const [searchParams] = useSearchParams();
  const siteKey = searchParams.get('siteKey');
  const token = searchParams.get('token');
  const shopperId = searchParams.get('shopperId') ?? searchParams.get('uid');
  const cartId = searchParams.get('cartId');
  const experienceId = searchParams.get('experienceId');

  const {
    smartOptinsExperience,
  } = useAppSelector((state) => state.smartOptins);
  const {
    siteExperienceConfiguration,
  } = useAppSelector((state) => state.globalExperience);
  const [screenToDisplay, setScreenToDisplay] = useState<SMART_OPTINS_SCREENS>(SMART_OPTINS_SCREENS.NO_FORM);
  const [isLoading, setIsLoading] = useState(true);
  const [displayError, setDisplayError] = useState(false);
  const suOptinForm = token !== null;

  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (siteKey !== null && shopperId !== null) {
        try {
          const [
            gamesInformations,
            siteData,
            smartOptinsExperienceFetched,
            personalData,
          ] = await Promise.all([
            cartId === null ? Promise.resolve(null) : experienceServiceInstance.getGamesInformations(shopperId, siteKey, cartId, experienceId),
            apiServiceInstance.fetchSiteData(siteKey),
            smartOptinsServiceInstance.getSmartOptinExperience(shopperId, siteKey),
            personalDataServiceInstance.getCustomerPersonalData(siteKey, shopperId, token),
          ]);

          if (
            smartOptinsExperienceFetched === null ||
            personalData === null ||
            personalData.optinList === null
          ) {
            // If smart opt-ins or personal data aren’t found, skip the collect screen and 
            // redirect the user to the manual email entry form
            dispatch(
              transitionAfterWelcomeScreen({
                collectScreenIsActivated: true,
                isSmartOptin: false,
              })
            );
            return;
          }

          if (suOptinForm) {
            if (
              personalData.email === undefined ||
              personalData.email === null
            ) {
              datadogLogs.logger.error('SU - Could not send shopperEmailSaved event because email is undefined or null', {
                shopperId,
              });
            } else {
              await eventServiceInstance.sendEmailEventToShopperEvent(
                siteKey,
                shopperId,
                smartOptinsExperienceFetched.experienceId,
                personalData.email
              );
            }
          }

          dispatch(setInitialExperienceData({
            games: gamesInformations ? gamesInformations.games : [],
            siteConfiguration: siteData.config.screens,
            site: siteData.site,
          }));
          dispatch(setSmartOptins({ smartOptinsExperience: smartOptinsExperienceFetched }));
          dispatch(setRetailerPersonalData({ retailerPersonalData: personalData }));

          if (Object.values(personalData.optinList).length > 0) {
            setScreenToDisplay(SMART_OPTINS_SCREENS.FORM);
            const optinKey = personalData.optinList[0].optinKey ?? personalData.optinList[0].key;
            const delayToSendOptinViewedEvent = 200;
            await new Promise((resolve) => {
              setTimeout(resolve, delayToSendOptinViewedEvent);
            });
            await eventServiceInstance.sendOptinViewedEventToShopperEvent(siteKey, shopperId, smartOptinsExperienceFetched.experienceId,optinKey);
          } else {
            if (suOptinForm && personalData.email !== null) {
              // If all optins already accepted, go to the game
              dispatch(transitionAfterWelcomeScreen({ 
                collectScreenIsActivated: false, 
                isSmartOptin: false, 
              }));
              // If error and no data from SU, display old optin screen
            } else if (personalData.email === null){
              dispatch(transitionAfterWelcomeScreen({ 
                collectScreenIsActivated: true, 
                isSmartOptin: false, 
              }));
            }
            setScreenToDisplay(SMART_OPTINS_SCREENS.NO_FORM);
          }
          setIsLoading(false);
        } catch (error) {
          Logger.error('Failed to fetch data', error);
          setDisplayError(true);
          setIsLoading(false);
        }
      } else {
        setDisplayError(true);
        setIsLoading(false);
      }
    };
      
    fetchData();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  if (displayError) {
    return <ErrorScreen config={getDefaultConfigurations().error}/>;
  }

  if (isLoading) {
    return <Loader/>;
  }

  return <>
    {
      siteExperienceConfiguration !== null && screenToDisplay === SMART_OPTINS_SCREENS.FORM &&
      <SmartOptinsForm
        suOptinForm={suOptinForm}
        siteKey={siteKey}
        config={siteExperienceConfiguration.smartOptinsForm}
        onSubmit={(values: CollectFormValues): void => {
          if (siteKey === null || shopperId === null || smartOptinsExperience === null) {
            setDisplayError(true);
            return;
          }
          if (Object.values(values.consents).length > 0) {
            collectServiceInstance.sendConsents(
              smartOptinsExperience.experienceId,
              values.consents,
              siteKey,
              shopperId,
              token ?? ''
            )
              .then(() => {
                setScreenToDisplay(SMART_OPTINS_SCREENS.POST_FORM);
                dispatch(transitionAfterCollectScreen());
              })
              .catch((err) => {
                setDisplayError(true);
                Logger.error(err);
              });
          } else {
            setScreenToDisplay(SMART_OPTINS_SCREENS.NO_FORM);
          }
        }}
      />
    }
    {
      siteExperienceConfiguration !== null && screenToDisplay === SMART_OPTINS_SCREENS.NO_FORM &&
      <SmartOptinsNoFormScreen config={siteExperienceConfiguration.smartOptinsNoForm} />
    }
    {
      siteExperienceConfiguration !== null && screenToDisplay === SMART_OPTINS_SCREENS.POST_FORM &&
      <SmartOptinsPostFormScreen config={siteExperienceConfiguration.smartOptinsPostForm} />
    }
  </>;
};
